import { useState, useEffect, createContext, useContext } from "react";
import { useAuth } from "../App";

const ThemeContext = createContext(null);

// Role-scope rules:
//   - Dark mode is a CUSTOMER-only preference. It is persisted per-user in
//     localStorage so one browser can host multiple users (admin logs in after
//     customer) without the admin inheriting the customer's dark surface.
//   - Admin / manager / chef / rider sessions ALWAYS force light theme,
//     regardless of what's in localStorage.
const ADMIN_ROLES = new Set(["admin", "manager", "chef", "rider"]);
const THEME_KEY = (uid) => `supermorsel_theme_${uid || "guest"}`;

export function ThemeProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?.user_id || null;
  const isStaff = user && ADMIN_ROLES.has(user.role);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(THEME_KEY(null)) || "light";
    } catch {
      return "light";
    }
  });

  // When user changes (login/logout/switch-account), load their own preference
  useEffect(() => {
    if (isStaff) {
      setTheme("light");
      return;
    }
    try {
      const saved = localStorage.getItem(THEME_KEY(userId)) || "light";
      setTheme(saved);
    } catch {
      setTheme("light");
    }
  }, [userId, isStaff]);

  // Apply / persist
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark" && !isStaff) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    try {
      if (!isStaff) localStorage.setItem(THEME_KEY(userId), theme);
    } catch { /* quota / disabled */ }
  }, [theme, userId, isStaff]);

  const toggleTheme = () => {
    if (isStaff) return; // admins cannot opt into dark theme
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isStaff }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
